import React, { useState } from "react";
import styled from "styled-components";
import {
  Modal as AntModal,
  Avatar,
  Button,
  Col,
  Flex,
  message,
  Row,
  Space,
  Tag,
} from "antd";
import { getFormattedNumber } from "../../../../utils";
import { IconCheckLight } from "../../../../assets/icons";
import api from "../../../../api/api";
import { CopyOutlined } from "@ant-design/icons";
import { useAtom } from "jotai";
import { orderCount } from "../../../../utils/atom";
import { useTranslation } from "react-i18next";

const Modal = styled(AntModal)`
  .ant-modal-title {
    font-size: 24px;
    font-weight: 700;
    color: #1e1e1e;
  }

  .ant-modal-close svg {
    font-size: 24px;
  }

  .ant-modal-body {
    .ant-btn-default {
      border: 1px solid #ffc8c8 !important;
      background: #ffe6e8 !important;
      color: #f00 !important;

      :hover {
        background: #ffe6e8 !important;
      }
    }
  }

  .ant-modal-footer .ant-row {
    .ant-col:first-child,
    .ant-col:nth-child(3) {
      text-align: left;
    }
    .ant-col:last-child {
      text-align: right;
    }

    .ant-btn-default {
      width: 100%;
      height: 56px;
      border: 1px solid #ffc8c8 !important;
      background: #ffe6e8 !important;
      color: #f00 !important;

      :hover {
        background: #ffe6e8 !important;
      }
    }

    .ant-btn-primary {
      width: 100%;
      height: 56px;
    }
  }
`;

const Text = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #1e1e1e;
`;

const BoldText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e1e1e;
`;

const TotalText = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #1e1e1e;
`;

const Card = styled.div`
  height: 100%;
  padding: 12px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  justify-content: flex-start;
  border-radius: 8px;
  background: #f7f7f7;

  .ant-descriptions-item {
    padding-bottom: 8px !important;
    color: #1d1014;
    font-size: 14px;
    font-weight: 400;
  }

  .ant-descriptions-item-label {
    font-size: 14px;
    font-weight: 700;
    color: #1e1e1e;
  }

  .ant-descriptions-item-content {
    font-size: 14px;
    font-weight: 400;
    color: #1d1014;
  }

  .ant-btn-primary {
    min-width: 172px;
    height: 48px;
  }

  .ss-image {
    width: 80px;
    aspect-ratio: 1;
    border-radius: 8px;
  }

  svg {
    cursor: pointer;
  }

  .custom-avatar {
    border: 1px solid #b9b9b9;
    border-radius: 8px;
    background: ${(props) => props.theme.backgroundColor};
  }
`;

const NormalText = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #1d1014;
  line-height: 1;
`;

const GrayText = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #868686;
  white-space: pre-wrap;
`;

const PickupModal = ({
  open,
  id,
  final_amount,
  products = [],
  handleClose,
  successCallback,
}) => {
  const [confirmLoading, setConfirmLoading] = useState(false);
    const [orderStatus, setOrderStatus] = useAtom(orderCount);
  
    const {t} = useTranslation();

  const handleConfirm = () => {
    if (!confirmLoading) {
      setConfirmLoading(true);
      api
        .confirmOrder(id,
          {
            order_status: "COMPLETED",
          }
        )
        .then(() => {
          setConfirmLoading(false);
          message.success(t('pickup_completed'));
          handleClose();
          successCallback();
          setOrderStatus(!orderStatus);
        })
        .catch((error) => {
          setConfirmLoading(false);
          message.error(error);
        });
    }
  };

  const onCancel = () => {
    !confirmLoading && handleClose();
  };
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(t('copy_to'));
    });
  };
  const filteredProducts = products?.filter((pd) => !pd.is_cancel);
  return (
    <>
      <Modal
        centered
        width={576}
        open={open}
        onCancel={onCancel}
        title={t('order_pickup_complete')}
        footer={
          <Row gutter={[16, 8]}>
            <Col xs={12}>
              <Text>{t('total_price')}</Text>
            </Col>
            <Col xs={12}>
              <TotalText>{`${getFormattedNumber(final_amount)}`}</TotalText>
            </Col>
            <Col xs={24}>
              <Button
                type="primary"
                icon={<IconCheckLight />}
                onClick={handleConfirm}
                loading={confirmLoading}
              >
                {t('confirm')}
              </Button>
            </Col>
          </Row>
        }
      >
        <Text style={{ paddingBottom: "12px" }}>
          {t('deliver_pickup_text')}
        </Text>
        <Flex style={{ flexDirection: "column", gap: 8 }}>
          {filteredProducts.map((product, key) => (
            <Card key={key}>
              <Avatar
                shape="square"
                size={100}
                src={
                  product.product.has_variant
                    ? product.variant.image_url
                    : product.product.thumb_image
                }
              />
              <Space direction="vertical" style={{ flex: 1 }}>
                <Row>
                  <Col xs={12}>
                    <GrayText
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy(product?.product?.sku);
                      }}
                      style={{ whiteSpace: "nowrap", cursor: "pointer" }}
                    >
                      {product?.product?.sku}
                      <CopyOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(product?.product?.sku);
                        }}
                        style={{
                          cursor: "pointer",
                          color: "gray",
                          paddingLeft: "4px",
                        }}
                      />
                    </GrayText>
                  </Col>
                </Row>
                <NormalText>{product?.product?.name?.primary}</NormalText>
                <Flex align="center" gap={2}>
                  {product?.variant?.ProductVariantCombinations?.map(
                    (v, key) => (
                      <Tag
                        style={{
                          backgroundColor: "white",
                          borderRadius: "8px",
                          border: "none",
                          padding: "4px 8px",
                          fontWeight: 500,
                        }}
                        key={key}
                      >
                        {v.variant_value?.name}
                      </Tag>
                    )
                  )}
                </Flex>
                <Row>
                  <Col xs={12}>
                    <NormalText>
                      {`${t('qty')}: ${getFormattedNumber(product.quantity)}`}
                    </NormalText>
                  </Col>
                  <Col xs={12} style={{ textAlign: "right" }}>
                    <BoldText>{`${getFormattedNumber(
                      product.price
                    )}`}</BoldText>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24}>{product.isCancelled && "Cancelled!"}</Col>
                </Row>
              </Space>
            </Card>
          ))}
        </Flex>
      </Modal>
    </>
  );
};

export default PickupModal;
